import { ClaudeCodeLauncher } from './claude-code.js'
import { CommandCodeLauncher } from './command-code.js'
import { CodexLauncher } from './codex.js'
import { OpenCodeLauncher } from './opencode.js'
import { KiloCodeLauncher } from './kilo-code.js'
import { AiderLauncher } from './aider.js'
import { AntigravityLauncher } from './antigravity.js'
import { OpenInterpreterLauncher } from './open-interpreter.js'
import { AmazonQLauncher } from './amazon-q.js'
import { CopilotLauncher } from './copilot.js'
import { ClineLauncher } from './cline.js'
import { WindsurfLauncher } from './windsurf.js'
import { GrokLauncher } from './grok.js'
import { FreebuffLauncher } from './freebuff.js'
import { RooCodeLauncher } from './roo-code.js'
import { CrushLauncher } from './crush.js'
import { PiLauncher } from './pi.js'
import type { ProviderLauncher } from './types.js'

/**
 * Provider registry — all supported AI coding provider launchers.
 *
 * To add a new provider:
 * 1. Create a class implementing ProviderLauncher
 * 2. Add it to the registry array below
 * 3. The detector will automatically discover it
 */
const registry: ProviderLauncher[] = [
  new ClaudeCodeLauncher(),
  new CommandCodeLauncher(),
  new CodexLauncher(),
  new OpenCodeLauncher(),
  new KiloCodeLauncher(),
  new AiderLauncher(),
  new AntigravityLauncher(),
  new OpenInterpreterLauncher(),
  new AmazonQLauncher(),
  new CopilotLauncher(),
  new ClineLauncher(),
  new WindsurfLauncher(),
  new GrokLauncher(),
  new FreebuffLauncher(),
  new RooCodeLauncher(),
  new CrushLauncher(),
  new PiLauncher(),
]

/** Information about a detected AI coding provider */
export interface DetectedProvider {
  /** The launcher instance */
  launcher: ProviderLauncher
  /** Installed version string, or null if unknown */
  version: string | null
}

/** Detect which AI coding providers are installed on the system */
export async function detectInstalledProviders(): Promise<DetectedProvider[]> {
  const results: DetectedProvider[] = []

  for (const launcher of registry) {
    const installed = await launcher.isInstalled()
    if (installed) {
      const version = await launcher.getVersion()
      results.push({ launcher, version })
    }
  }

  return results
}

/** Get a launcher by its internal name */
export function getLauncher(name: string): ProviderLauncher | undefined {
  return registry.find((l) => l.name === name)
}

/** Get all registered launchers (installed or not) */
export function getAllLaunchers(): ProviderLauncher[] {
  return [...registry]
}

/** Register a custom provider launcher at runtime */
export function registerLauncher(launcher: ProviderLauncher): void {
  if (registry.some((l) => l.name === launcher.name)) {
    throw new Error(`Provider "${launcher.name}" is already registered`)
  }
  registry.push(launcher)
}
